import { useCallback, useMemo } from "react";
import { useContractABI } from "./useContractABI";
interface EventInfo {
  name: string;
  type: string;
  inputs?: Array<{
    name: string;
    type: string;
    indexed?: boolean;
  }>;
}
interface UseContractEventsOptions {
  chainId?: number;
  address?: string;
  enabled?: boolean;
  selectedEventJson?: string;
}
interface UseContractEventsResult {
  events: EventInfo[];
  isLoading: boolean;
  error: string | null;
  status: "idle" | "loading" | "success" | "error" | "not-found";
  selectedEvent: EventInfo | null;
  selectedEventName: string;
  selectEvent: (event: EventInfo) => string;
  hasEvents: boolean;
}

/**
 * Hook to fetch and manage contract events
 * Built on top of useContractABI but focused on events
 */
export function useContractEvents({
  chainId,
  address,
  enabled = true,
  selectedEventJson
}: UseContractEventsOptions): UseContractEventsResult {
  const {
    events,
    isLoading,
    error,
    status
  } = useContractABI({
    chainId,
    address,
    enabled
  });

  // Parse the selected event from JSON
  const selectedEvent = useMemo<EventInfo | null>(() => {
    if (!selectedEventJson) return null;
    try {
      const parsed = typeof selectedEventJson === "string" ? JSON.parse(selectedEventJson) : selectedEventJson;
      if (parsed && parsed.type === "event" && parsed.name) {
        return parsed;
      }
    } catch (e) {
      console.error("[useContractEvents] Failed to parse selected event:", e);
    }
    return null;
  }, [selectedEventJson]);
  const selectedEventName = selectedEvent?.name || "";

  // Helper to select an event and return JSON string
  const selectEvent = useCallback((event: EventInfo): string => {
    return JSON.stringify(event, null, 2);
  }, []);
  return {
    events,
    isLoading,
    error,
    status,
    selectedEvent,
    selectedEventName,
    selectEvent,
    hasEvents: events.length > 0
  };
}